import { createClient, type SupabaseClient } from "@supabase/supabase-js";

let supabaseInstance: SupabaseClient | null = null;
let configPromise: Promise<void> | null = null;

export function setSupabaseForTesting(client: SupabaseClient | null): void {
  supabaseInstance = client;
  if (client === null) {
    configPromise = null;
  }
}

/**
 * Initialize Supabase client by fetching config from server
 */
export async function initSupabase(): Promise<SupabaseClient> {
  if (supabaseInstance) return supabaseInstance;

  // Ensure we only fetch config once
  if (!configPromise) {
    configPromise = fetch("/api/config")
      .then((res) => res.json())
      .then((config) => {
        if (!config.SUPABASE_URL || !config.SUPABASE_ANON_KEY) {
          throw new Error("Missing Supabase configuration from server");
        }

        // Conditionally disable detectSessionInUrl on third-party OAuth callbacks
        // Problem: Facebook OAuth returns #access_token which Supabase tries to parse as its own token
        // Solution: Disable URL detection only on third-party OAuth callback routes
        // Reference: https://github.com/supabase/supabase-js/issues/831
        const isThirdPartyOAuthCallback =
          typeof window !== "undefined" &&
          (window.location.pathname.includes("/facebook-instagram/callback") ||
            window.location.pathname.includes("/instagram/callback"));

        supabaseInstance = createClient(config.SUPABASE_URL, config.SUPABASE_ANON_KEY, {
          auth: {
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: !isThirdPartyOAuthCallback,
          },
        });
      });
  }

  await configPromise;

  if (!supabaseInstance) {
    throw new Error("Failed to initialize Supabase");
  }

  return supabaseInstance;
}

/**
 * Get Supabase client instance (must call initSupabase first)
 */
export function getSupabase(): SupabaseClient {
  if (!supabaseInstance) {
    throw new Error("Supabase not initialized. Call initSupabase() first.");
  }
  return supabaseInstance;
}
